import { NextPage } from 'next';
import React from 'react';
import {
  BreakpointQueryName,
  Button,
  ButtonColor,
  FontWeight, Grid, Image, Spacing, SpacingName, Typography,
} from '@doveit/lego';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import { useTheme } from 'styled-components';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import LandingLayout from '../client/layouts/landing-layout/LandingLayout';
import {
  BackgroundImage, CTA, TextAlign, WrapperSize,
} from '../client/types';
import PageSection from '../client/components/page-section/PageSection';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import { AppNavigationVariant } from '../client/templates/common/app-navigation/types';
import Hero from '../client/slices/hero/Hero';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import FeatureBlock from '../client/blocks/feature-block/FeatureBlock';
import Stripe from '../client/slices/stripe/Stripe';
import * as styles from '../client/templates/franchising/Franchising.style';
import TrustBoxMini from '../client/components/trustpilot/trustbox-mini/TrustBoxMini';
import { PackageCardProps } from '../client/components/package-card/PackageCard';
import SaveJobApplication from '../client/containers/save-job-application/SaveJobApplication';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { JobPosition } from '../domain/graphcms';
import { JOB_POSITION_QUERY } from '../client/graphql/queries/job-offer';
import { JobApplication } from '../services/dtos';
import { createJobApplicationEventLabel, sendJobApplicationEvent } from '../client/services/tracking/jobApplicationTracking';
import { JOB_APPLICATION_STORAGE_KEY } from './become-agent';
import {
  JOIN_OUR_AGENTS_INFO_AS, BECOME_AGENT_INFO_URL, JOIN_OUR_AGENTS_AS, LANDING_JOIN_OUR_AGENTS_AS, JOIN_OUR_AGENTS_URL, goToPage,
} from '../client/utils/links';
import AppLayout from '../client/layouts/app-layout/AppLayout';

export type PackageOffer = PackageCardProps & { id: string };

export interface FranchisingPageProps {
  isLanding: boolean,
  jobPosition: JobPosition,
  SEO: PageHeadProps['SEO'],
  hero: {
    title: string,
    description: string,
    cover: BackgroundImage,
    cta: CTA,
  },
  intro: {
    title: string,
    subtitle: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
  },
  reputationSection: {
    title: string,
    description: string,
  },
  networkSection: {
    title: string,
    subtitle: string,
    headline: string,
    description: string,
  },
  advantages: {
    title: string,
    subtitle: string,
    items: {
      [key: string]: {
        title: string,
        subtitle?: string,
        highlight?: string,
        description?: string,
        image?: {
          title?: string,
          url: string,
          alt: string,
        },
      },
    },
  },
  jobOfferSection: {
    title: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTA,
  },
  contactSection: {
    title: string,
  },
}

export const FranchisingPage: NextPage<FranchisingPageProps> = ({
  isLanding,
  jobPosition,
  SEO,
  hero,
  intro,
  jobOfferSection,
  networkSection,
  reputationSection,
  advantages,
  contactSection,
}) => {
  const { current, ref: formRef } = useRefInView({
    threshold: 0,
  });

  const theme = useTheme();
  const isTabletOrBigger = useMediaQuery(theme.breakpoints.up(BreakpointQueryName.LG));

  const goToJoinOurAgentsPage = React.useCallback(() => {
    if (isLanding) {
      goToPage({
        url: {
          pathname: JOIN_OUR_AGENTS_URL,
          query: { isLanding },
        },
        as: LANDING_JOIN_OUR_AGENTS_AS,
      });
    } else {
      goToPage({
        url: JOIN_OUR_AGENTS_URL,
        as: JOIN_OUR_AGENTS_AS,
      });
    }
  }, [isLanding]);

  const goToJobApplicationInfoStep = React.useCallback((jobApplication: JobApplication) => {
    const eventLabel = createJobApplicationEventLabel('main', jobPosition, jobApplication.province);
    sendJobApplicationEvent(eventLabel);

    sessionStorage.setItem(JOB_APPLICATION_STORAGE_KEY, JSON.stringify(jobApplication));

    goToPage({
      url: BECOME_AGENT_INFO_URL,
      as: JOIN_OUR_AGENTS_INFO_AS,
    });
  }, [jobPosition]);

  const Layout = isLanding ? LandingLayout : AppLayout;

  return (
    <Layout navigationVariant={AppNavigationVariant.LIGHT}>
      <PageHead SEO={SEO} />
      {/** Hero */}
      <Hero
        cover={requireResourcesFromStaticAssets(hero.cover)}
        coverPosition="top left"
      >
        <Typography.H1 color="neutrals.lightest">
          {hero.title}
        </Typography.H1>
        <Spacing margin={[SpacingName.SMALLER, 0, SpacingName.MEDIUM]}>
          <Typography.H3
            color="neutrals.lightest"
            tagName="p"
            fontWeight={FontWeight.LIGHT}
          >
            {hero.description}
          </Typography.H3>
        </Spacing>
        <Button
          {...hero.cta}
          color={ButtonColor.LIGHT}
          onClick={scrollToRef(current)}
        />
      </Hero>

      {/** Intro */}
      <PageSection
        padding={[SpacingName.BIGGER, 0]}
        aria-label="Intro"
      >
        <PageSection.Wrapper>
          <Grid
            gutter={SpacingName.MEDIUM}
          >
            <Grid.Unit size={{ LG: 3 / 4 }}>
              <Typography.H2>
                {intro.title}
              </Typography.H2>
              <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                <Typography.H2
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {intro.subtitle}
                </Typography.H2>
              </Spacing>
              <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {intro.description}
                </Typography.H4>
              </Spacing>
            </Grid.Unit>
            {isTabletOrBigger && (
              <Grid.Unit size={{ LG: 1 / 4 }}>
                <div css={styles.introImage}>
                  <Image
                    src={requireFromStaticAsset(intro.image.url)}
                    alt={intro.image.alt}
                    lazyLoad
                    responsive
                  />
                </div>

              </Grid.Unit>
            )}
          </Grid>
        </PageSection.Wrapper>
      </PageSection>

      <div css={styles.divider} />

      {/** Vantaggi */}
      <div aria-label="Vantaggi">
        <h2 css={styles.advantagesTitle}>
          {advantages.title}
        </h2>
        <div css={styles.advantagesSubtitle}>
          {advantages.subtitle}
        </div>
        <Stripe
          imageURL={requireFromStaticAsset(advantages.items.appointments.image.url)}
          imageAlt={advantages.items.appointments.image.alt}
        >
          <Typography.H2
            tagName="h3"
            uppercase
            color="brand.primary"
          >
            {advantages.items.appointments.title}
          </Typography.H2>
          <Spacing margin={[SpacingName.SMALL, 0, 0]}>
            <Typography.H3
              tagName="p"
              fontWeight={FontWeight.LIGHT}
            >
              {advantages.items.appointments.description}
            </Typography.H3>
          </Spacing>
        </Stripe>
        <PageSection padding={[SpacingName.BIGGER, 0]}>
          <PageSection.Wrapper>
            <FeatureBlock
              title={{
                tag: 'h3',
                content: advantages.items.aggregators.title,
              }}
              highlight={advantages.items.aggregators.highlight}
              description={advantages.items.aggregators.description}
            >
              <Image
                src={requireFromStaticAsset(advantages.items.aggregators.image.url)}
                alt={advantages.items.aggregators.image.alt}
                lazyLoad
              />
            </FeatureBlock>
          </PageSection.Wrapper>
        </PageSection>
        <Stripe
          imageURL={requireFromStaticAsset(advantages.items.fee.image.url)}
          imageAlt={advantages.items.fee.image.alt}
        >
          <Typography.H2
            tagName="h3"
            uppercase
            color="brand.primary"
          >
            {advantages.items.fee.title}
          </Typography.H2>
          <Spacing margin={[SpacingName.SMALL, 0, 0]}>
            <Typography.H3
              tagName="p"
              fontWeight={FontWeight.LIGHT}
            >
              {advantages.items.fee.highlight}
            </Typography.H3>
          </Spacing>
        </Stripe>
        <PageSection padding={[SpacingName.BIGGER, 0]}>
          <PageSection.Wrapper>
            <FeatureBlock
              title={{
                tag: 'h3',
                content: advantages.items.technology.title,
              }}
              highlight={advantages.items.technology.highlight}
              description={advantages.items.technology.description}
            >
              <Image
                src={requireFromStaticAsset(advantages.items.technology.image.url)}
                alt={advantages.items.technology.image.alt}
                lazyLoad
              />
            </FeatureBlock>
          </PageSection.Wrapper>
        </PageSection>
        <Spacing padding={[SpacingName.MEDIUM, 0]}>
          <div css={styles.divider} />
        </Spacing>
        <Stripe
          imageURL={requireFromStaticAsset(advantages.items.network.image.url)}
          imageAlt={advantages.items.network.image.alt}
        >
          <Typography.H2
            tagName="h3"
            uppercase
            color="brand.primary"
          >
            {advantages.items.network.title}
          </Typography.H2>
          <Spacing margin={[SpacingName.SMALL, 0, 0]}>
            <Typography.H3
              tagName="p"
              fontWeight={FontWeight.LIGHT}
            >
              {advantages.items.network.highlight}
            </Typography.H3>
          </Spacing>
        </Stripe>
        <PageSection padding={[SpacingName.MEDIUM, 0]}>
          <PageSection.Wrapper>
            <div css={styles.networkDetails}>
              <Typography.H4
                tagName="p"
                fontWeight={FontWeight.LIGHT}
              >
                {advantages.items.network_details.description}
              </Typography.H4>
            </div>
          </PageSection.Wrapper>
        </PageSection>
      </div>

      <div css={styles.divider} />

      {/** Reputation Section */}
      <PageSection
        padding={[SpacingName.BIGGER, 0]}
        aria-label="Reputazione"
      >
        <PageSection.Wrapper>
          <FeatureBlock
            title={{
              tag: 'h2',
              content: reputationSection.title,
            }}
            description={reputationSection.description}
          >
            <div css={styles.reputationTrustBox}>
              <TrustBoxMini clickable={!isLanding} />
            </div>
          </FeatureBlock>
        </PageSection.Wrapper>
      </PageSection>

      <div css={styles.divider} />

      {/** Network section */}
      <PageSection
        padding={[SpacingName.BIGGER, 0]}
        aria-label="Rete"
      >
        <PageSection.Wrapper>
          <Grid
            gutter={SpacingName.MEDIUM}
          >
            <Grid.Unit>
              <Typography.H2>
                {networkSection.title}
              </Typography.H2>
              <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                <Typography.H2
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {networkSection.subtitle}
                </Typography.H2>
              </Spacing>
              <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                <Typography.H3
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {networkSection.headline}
                </Typography.H3>
              </Spacing>
              <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {networkSection.description}
                </Typography.H4>
              </Spacing>
            </Grid.Unit>
          </Grid>
        </PageSection.Wrapper>
      </PageSection>

      {/** Agent Job Offer section */}
      <Stripe
        imageURL={requireFromStaticAsset(jobOfferSection.image.url)}
        imageAlt={jobOfferSection.image.alt}
      >
        <Typography.H2
          color="brand.primary"
          uppercase
        >
          {jobOfferSection.title}
        </Typography.H2>
        <Spacing margin={[SpacingName.SMALL, 0]}>
          <Typography.H4
            tagName="p"
            fontWeight={FontWeight.LIGHT}
          >
            {jobOfferSection.description}
          </Typography.H4>
        </Spacing>
        <Button
          {...jobOfferSection.cta}
          color={ButtonColor.PRIMARY}
          onClick={goToJoinOurAgentsPage}
          href={!isLanding ? JOIN_OUR_AGENTS_URL : undefined}
        />
      </Stripe>

      {/** Modulo di contatto */}
      <div ref={formRef}>
        <PageSection
          aria-label="Modulo di contatto"
          padding={[SpacingName.BIGGER, 0]}
        >
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {contactSection.title}
            </PageSection.Title>
          </PageSection.Wrapper>

          <PageSection.Wrapper maxWidth={WrapperSize.M}>
            <PageSection.Block>
              <SaveJobApplication
                ctaLabel="Invia la tua richiesta"
                jobPositionId={jobPosition.id}
                showProvince
                dataTracking={isLanding ? 'landing-franchising-form-main-submit' : 'franchising-form-main-submit'}
                submitColor={ButtonColor.PRIMARY}
                onSuccess={goToJobApplicationInfoStep}
              />
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      </div>
    </Layout>
  );
};

export const getServerSideProps = withGetServerSideError<FranchisingPageProps>(async ({ query }) => {
  const isLanding = (query?.isLanding as string)?.toLowerCase() === 'true';
  const [data] = await Promise.all([
    importDynamicData('pages/franchising.yml'),
    importDynamicData('common.yml'),
  ]);

  const graphQLClient = initGraphQLClient();

  const { data: { jobPosition } } = await graphQLClient.query<{ jobPosition: JobPosition }>({
    query: JOB_POSITION_QUERY,
    variables: {
      id: data.jobPositionId,
    },
  });

  return {
    props: {
      ...data,
      SEO: {
        ...data.SEO,
        robots: isLanding ? ['noindex'] : null,
      },
      jobPosition,
      isLanding,
    },
  };
});

export default withError(FranchisingPage);
